// @ts-nocheck
import * as React from "react";
import { Logo, siteName, brandParts, Ic, PasswordInput, PhoneInput, AppFooter, apiErrorMessage, OrgCombobox } from "./common";
import { LocationPicker } from "../components/naver/LocationPicker";

// /signup — 5 role cards → form → submit
// citizen/guardian: auto-login + redirect to default_path
// manager/agency: pending approval message
// region_admin/system_admin: blocked (admin invite only)

const ROLES = [
  { v: "citizen",      label: "어르신 본인",   sub: "케어 신청자",          icon: "user",    color: "orange", canSelf: true },
  { v: "guardian",     label: "보호자",       sub: "자녀·배우자·가족",     icon: "heart",   color: "rose",   canSelf: true },
  { v: "manager",      label: "케어매니저",    sub: "요양보호사·사회복지사", icon: "manager", color: "green",  canSelf: true,  needsApproval: true },
  { v: "agency",       label: "유관기관",     sub: "병원·보건소·요양시설", icon: "org",     color: "sky",    canSelf: true,  needsApproval: true },
  { v: "region_admin", label: "권역 관리자",   sub: "협회 운영자 (초대전용)", icon: "region",  color: "amber",  canSelf: false },
];

function Signup() {
  const [role, setRole] = React.useState("guardian");
  const [form, setForm] = React.useState({
    email: "", password: "", name: "", phone: "",
    // elderly (citizen/guardian)
    age: "", address: "", household_type: "", health_status: "", elderly_name: "", elderly_phone: "",
    // guardian
    relation: "", connect_mode: "new", connect_code: "",
    // 어르신 본인(citizen) 가입 시 입력하는 보호자 정보 — 필수
    guardian_name: "", guardian_phone: "",
    // manager — license_no 타이핑 폐지, 자격증 사진(licenseFile)으로 대체
    qualification: "", region_id: "", organization_id: "",
    // agency
    job_title: "", organization_name: "", organization_category: "",
  });
  const [orgs, setOrgs] = React.useState([]);
  const [regions, setRegions] = React.useState([]);
  const [consent, setConsent] = React.useState({ terms: false, privacy: false, sensitive: false });
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [licenseFile, setLicenseFile] = React.useState(null); // 매니저 자격증 사진(1장)
  // 비밀번호 확인(재입력) — 서버에는 보내지 않고 클라이언트에서 일치만 검사한다.
  const [passwordConfirm, setPasswordConfirm] = React.useState("");
  // 빈 필수 항목 추적 — { fieldKey: true }. 가입 버튼 클릭 시 채워지고, 해당
  // 항목을 입력하면 즉시 해제된다. 빨간 테두리 + 첫 항목 포커스에 사용.
  const [errs, setErrs] = React.useState({});
  const formRef = React.useRef(null);

  React.useEffect(() => {
    // minimal=1 — {id,name,region_id,category}만 받는 경량 응답. 전체 직렬화(행당
    // trends·contact N+1 + 10MB)를 우회한다. 드롭다운/검색에 필요한 필드만 쓰므로 충분.
    fetch("/api/v1/organizations?minimal=1").then(r => r.json()).then(setOrgs);
    fetch("/api/v1/regions").then(r => r.json()).then(setRegions);
  }, []);

  const roleMeta = ROLES.find(r => r.v === role);
  // 값 변경 시 해당 항목의 에러 표시를 해제(사용자가 채우는 즉시 빨간 테두리 제거).
  const clearErr = (k) => setErrs(e => (e[k] ? { ...e, [k]: false } : e));
  const set = (k) => (v) => { setForm(f => ({ ...f, [k]: v })); clearErr(k); };
  const allAgreed = consent.terms && consent.privacy && consent.sensitive;

  // 빈 필수 항목이면 빨간 테두리 스타일을 반환. 일반 input/select/PhoneInput은
  // style로, PasswordInput은 error prop으로 전달한다.
  const eStyle = (k) => errs[k]
    ? { borderColor: 'var(--rose-500)', boxShadow: '0 0 0 2px rgba(244,63,94,0.15)' }
    : undefined;

  const HOUSEHOLDS = ["독거", "노인부부", "가족동거", "시설", "기타"];
  // 어르신 정보 입력 묶음 — citizen(본인) 및 guardian(신규 어르신 등록) 공용.
  const elderlyFields = (withName) => (<>
    {withName && (
      <div style={{gridColumn:'1/-1'}}>
        <div className="label">어르신 성함</div>
        <input className="input" data-field="elderly_name" style={eStyle('elderly_name')} value={form.elderly_name} onChange={e=>set('elderly_name')(e.target.value)} />
      </div>
    )}
    {/* 어르신 본인 연락처 — 보호자가 신규 어르신을 등록할 때만 입력(선택).
        citizen 본인 가입은 계정 연락처가 곧 본인 번호라 별도 입력란을 두지 않는다. */}
    {withName && (
      <div style={{gridColumn:'1/-1'}}>
        <div className="label">어르신 본인 연락처 (선택)</div>
        <PhoneInput value={form.elderly_phone} onChange={e=>set('elderly_phone')(e.target.value)}/>
      </div>
    )}
    <div>
      <div className="label">나이</div>
      <input className="input" data-field="age" style={eStyle('age')} type="number" value={form.age} onChange={e=>set('age')(e.target.value)} placeholder="세"/>
    </div>
    <div>
      <div className="label">가구형태</div>
      <select className="select" data-field="household_type" style={eStyle('household_type')} value={form.household_type} onChange={e=>set('household_type')(e.target.value)}>
        <option value="">— 선택 —</option>
        {HOUSEHOLDS.map(h => <option key={h}>{h}</option>)}
      </select>
    </div>
    <div style={{gridColumn:'1/-1'}}>
      <div className="label">주소</div>
      <input className="input" data-field="address" style={eStyle('address')} value={form.address} onChange={e=>set('address')(e.target.value)} placeholder="시/군/구 동 ..."/>
    </div>
    <div style={{gridColumn:'1/-1'}}>
      <div className="label">소속 권역</div>
      <select className="select" data-field="region_id" style={eStyle('region_id')} value={form.region_id} onChange={e=>set('region_id')(e.target.value)}>
        <option value="">— 선택 —</option>
        {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
    </div>
    <div style={{gridColumn:'1/-1'}} data-field="location" tabIndex={-1}>
      <div className="label">위치 (지도에서 지정 — 매칭 거리 계산에 사용)</div>
      {/* 좌표는 필수: 미지정 시 지도 박스에 빨간 테두리를 둘러 강조한다. */}
      <div style={errs.location
        ? {border:'1.5px solid var(--rose-500)', borderRadius:10, padding:6, boxShadow:'0 0 0 2px rgba(201,89,79,0.15)'}
        : undefined}>
        {(() => {
          const reg = regions.find(r => String(r.id) === String(form.region_id));
          const regionCenter = reg && reg.center_latitude != null && reg.center_longitude != null
            ? { lat: reg.center_latitude, lng: reg.center_longitude } : null;
          const value = form.latitude != null && form.longitude != null
            ? { lat: form.latitude, lng: form.longitude } : null;
          return (
            <LocationPicker
              value={value}
              center={regionCenter}
              onChange={(c) => { setForm(f => ({ ...f, latitude: c.lat, longitude: c.lng })); clearErr('location'); }}
            />
          );
        })()}
      </div>
      {errs.location && <div style={{fontSize:11.5, color:'var(--rose-500)', marginTop:4}}>지도에서 위치를 지정해주세요.</div>}
    </div>
    <div style={{gridColumn:'1/-1'}}>
      <div className="label">건강상태 (선택)</div>
      <input className="input" value={form.health_status} onChange={e=>set('health_status')(e.target.value)} placeholder="예: 고혈압·당뇨, 거동 불편"/>
    </div>
  </>);

  // 어르신 본인(citizen) 가입 시 입력하는 보호자(비상 연락) 정보 — 필수.
  // 보호자 역할 가입은 본인이 곧 보호자라 이 블록을 쓰지 않는다.
  const RELATIONS = ["자녀", "배우자", "형제·자매", "친척", "기타"];
  const guardianFields = (<>
    <div style={{gridColumn:'1/-1'}}>
      <div className="label">보호자 성함</div>
      <input className="input" data-field="guardian_name" style={eStyle('guardian_name')} value={form.guardian_name} onChange={e=>set('guardian_name')(e.target.value)} placeholder="비상시 연락할 보호자"/>
    </div>
    <div>
      <div className="label">보호자 연락처</div>
      <PhoneInput data-field="guardian_phone" style={eStyle('guardian_phone')} value={form.guardian_phone} onChange={e=>set('guardian_phone')(e.target.value)}/>
    </div>
    <div>
      <div className="label">보호자와의 관계</div>
      <select className="select" data-field="relation" style={eStyle('relation')} value={form.relation} onChange={e=>set('relation')(e.target.value)}>
        <option value="">— 선택 —</option>
        {RELATIONS.map(rel => <option key={rel}>{rel}</option>)}
      </select>
    </div>
  </>);

  // 역할별 필수 항목 목록 — [필드키, 라벨]. (선택) 표시 항목은 제외한다.
  const requiredFields = () => {
    const f = [["name","이름"], ["phone","연락처"], ["email","이메일"], ["password","비밀번호"]];
    if (role === "citizen") {
      f.push(["age","나이"], ["household_type","가구형태"], ["address","주소"], ["region_id","소속 권역"], ["location","위치(지도 좌표)"]);
      f.push(["guardian_name","보호자 성함"], ["guardian_phone","보호자 연락처"], ["relation","보호자와의 관계"]);
    }
    if (role === "guardian") {
      f.push(["relation","어르신과의 관계"]);
      if (form.connect_mode === "existing") f.push(["connect_code","연결코드"]);
      else f.push(["elderly_name","어르신 성함"], ["age","나이"], ["household_type","가구형태"], ["address","주소"], ["region_id","소속 권역"], ["location","위치(지도 좌표)"]);
    }
    if (role === "manager") {
      // 소속 권역은 소속 기관에서 파생되므로 기관만 필수로 검사한다.
      f.push(["qualification","자격증"], ["license","자격증 사진"], ["organization_id","소속 기관"]);
    }
    if (role === "agency") {
      f.push(["job_title","기관 내 직책"]);
      if (!form.organization_id) f.push(["organization_name","신규 기관명"], ["organization_category","기관 분류"], ["region_id","소속 권역"]);
    }
    return f;
  };
  const isEmptyField = (k) => {
    if (k === "license") return !licenseFile;
    if (k === "location") return form.latitude == null || form.longitude == null; // 지도 좌표 미지정
    return !String(form[k] ?? "").trim();
  };
  const focusField = (k) => {
    const el = formRef.current?.querySelector(`[data-field="${k}"]`);
    if (el) { el.focus?.(); el.scrollIntoView?.({ behavior: "smooth", block: "center" }); }
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null);
    if (!roleMeta.canSelf) { setErr("이 역할은 운영자 초대로만 가입 가능합니다."); return; }

    // 1) 빈 필수 항목 검사 — 빨간 테두리 + 첫 항목으로 포커스 이동.
    const missing = requiredFields().filter(([k]) => isEmptyField(k));
    if (missing.length) {
      const map = {};
      missing.forEach(([k]) => { map[k] = true; });
      setErrs(map);
      setErr(`입력하지 않은 필수 항목이 ${missing.length}개 있습니다. 빨간색으로 표시된 항목을 확인해주세요.`);
      focusField(missing[0][0]);
      return;
    }
    setErrs({});

    if (!allAgreed) { setErr("필수 동의 항목(이용약관·개인정보·민감정보)에 모두 동의해주세요."); return; }
    if (form.password.length < 6) { setErr("비밀번호는 최소 6자 이상이어야 합니다."); focusField("password"); return; }
    if (!(/[A-Za-z]/.test(form.password) && /\d/.test(form.password))) {
      setErr("비밀번호에 영문과 숫자가 모두 포함되어야 합니다."); focusField("password"); return;
    }
    if (form.password !== passwordConfirm) {
      setErrs(e => ({ ...e, password_confirm: true }));
      setErr("비밀번호 확인이 일치하지 않습니다. 다시 입력해주세요."); focusField("password_confirm"); return;
    }

    setSubmitting(true);
    try {
      const fields = {
        role, ...form,
        agree_terms: consent.terms, agree_privacy: consent.privacy, agree_sensitive: consent.sensitive,
      };
      // 매니저는 자격증 사진(파일)을 함께 보내야 하므로 멀티파트로 제출. 그 외 역할은 JSON.
      let r;
      if (role === "manager") {
        const fd = new FormData();
        Object.entries(fields).forEach(([k, v]) => { if (v !== null && v !== undefined) fd.append(k, String(v)); });
        if (licenseFile) fd.append("license_image", licenseFile);
        r = await fetch("/api/v1/signups", { method: "POST", body: fd, credentials: "same-origin" });
      } else {
        r = await fetch("/api/v1/signups", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
          credentials: "same-origin",
        });
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        // 사용자에겐 기계용 코드(j.error: "invalid" 등)를 노출하지 않는다.
        // 우선순위: 구체적 검증 메시지(details) → 서버 친화 메시지(message)
        // → 코드별 한글 폴백 → 일반 메시지.
        const CODE_KO = {
          invalid: "입력하신 정보를 다시 확인해주세요.",
          invalid_profile: "입력 정보를 다시 확인해주세요.",
          role_not_self_signup: "관리자 계정은 운영자 초대로만 가입할 수 있습니다.",
          consent_required: "필수 동의 항목에 모두 동의해주세요.",
        };
        const msg =
          (j.details && j.details.length && j.details.join("\n")) ||
          j.message ||
          CODE_KO[j.error] ||
          `가입 처리 중 오류가 발생했습니다 (${r.status})`;
        setErr(msg);
        return;
      }
      setResult(j);
      // citizen/guardian: auto-login → redirect
      if (j.status === "active") {
        setTimeout(() => { window.location.href = j.user?.default_path || "/"; }, 1200);
      }
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setSubmitting(false); }
  };

  // ── Success screen ─────────────────
  if (result?.status === "pending_approval") {
    return (
      <div className="app" style={{display:'grid', placeItems:'center', height:'100%', background:'var(--bg)', padding:40}}>
        <div className="card" style={{maxWidth:480, width:'100%', padding:36, textAlign:'center'}}>
          <div style={{width:64, height:64, borderRadius:'50%', background:'var(--orange-50)', color:'var(--orange-600)', display:'grid', placeItems:'center', margin:'0 auto 16px'}}>
            <span style={{width:32, height:32, display:'inline-block'}}>{Ic.check}</span>
          </div>
          <div className="h2" style={{fontSize:22, marginBottom:8}}>가입 신청 접수됨</div>
          <div className="muted" style={{fontSize:13, lineHeight:1.65, marginBottom:24}}>{result.message}</div>
          <div style={{padding:14, background:'var(--bg-sub)', borderRadius:10, fontSize:12, marginBottom:20, textAlign:'left'}}>
            <div className="muted" style={{marginBottom:4, fontWeight:600}}>접수 내용</div>
            <div><b>{result.user.name}</b> · {result.user.role_label}</div>
            <div className="muted">{result.user.email}</div>
          </div>
          <a href="/" className="btn btn-ghost btn-md" style={{textDecoration:'none', marginRight:8}}>홈으로</a>
          <a href="/login" className="btn btn-primary btn-md" style={{textDecoration:'none'}}>로그인 화면</a>
        </div>
      </div>
    );
  }
  if (result?.status === "active") {
    return (
      <div className="app" style={{display:'grid', placeItems:'center', height:'100%', background:'var(--bg)', padding:40}}>
        <div className="card" style={{maxWidth:480, width:'100%', padding:36, textAlign:'center'}}>
          <div style={{width:64, height:64, borderRadius:'50%', background:'var(--green-50)', color:'var(--green-600)', display:'grid', placeItems:'center', margin:'0 auto 16px'}}>
            <span style={{width:32, height:32, display:'inline-block'}}>{Ic.check}</span>
          </div>
          <div className="h2" style={{fontSize:22, marginBottom:8}}>가입 완료</div>
          <div className="muted" style={{fontSize:13, lineHeight:1.65}}>잠시 후 자동으로 이동합니다…</div>
        </div>
      </div>
    );
  }

  // ── Form ───────────────────────────
  return (
    <div className="app" style={{display:'flex', flexDirection:'column', height:'100%', background:'var(--bg)'}}>
    {/* 좌(브랜드)/우(폼) 2단은 데스크톱 전용. 좁은 화면에서는 .signup-grid 미디어쿼리가
        1단으로 접고 브랜드 패널을 숨긴다(로그인 화면 .auth-grid 와 동일한 규칙). */}
    <div className="signup-grid">
      {/* Left — brand */}
      <div className="signup-brand" style={{padding:'48px 56px', background:'linear-gradient(160deg, #2A241F 0%, #3F342C 60%, #5A4434 100%)', color:'#F5EBD8', display:'flex', flexDirection:'column'}}>
        <a href="/" style={{display:'flex', alignItems:'center', gap:10, textDecoration:'none', color:'inherit'}}>
          <div className="brand-mark" style={{width:32, height:32}}>{brandParts(siteName()).mark}</div>
          <div>
            <div style={{fontWeight:800, fontSize:15, color:'#fff'}}>{brandParts(siteName()).name}</div>
            {brandParts(siteName()).sub && <div style={{fontSize:10, color:'#A89A7C', letterSpacing:'0.06em'}}>{brandParts(siteName()).sub}</div>}
          </div>
        </a>

        <div style={{marginTop:60}}>
          <div style={{fontSize:11, color:'var(--orange-300)', fontWeight:700, letterSpacing:'0.08em', marginBottom:14}}>회원가입</div>
          <h1 style={{fontSize:32, fontWeight:800, lineHeight:1.25, letterSpacing:'-0.02em', margin:0, color:'#fff'}}>
            한 권역의 돌봄,<br/>지금 함께하세요.
          </h1>
          <p style={{fontSize:13, lineHeight:1.7, color:'#D4C9B2', marginTop:18, maxWidth:380}}>
            어르신·보호자는 즉시 시작할 수 있고, 케어매니저·유관기관은 권역 관리자 승인 후 활성화됩니다.
          </p>
        </div>

        <div style={{marginTop:'auto', padding:18, background:'rgba(255,255,255,0.06)', borderRadius:14, border:'1px solid rgba(255,255,255,0.08)'}}>
          <div style={{fontSize:11, color:'#EFB287', fontWeight:700, letterSpacing:'0.06em', marginBottom:10}}>가입 후 첫 단계</div>
          <div style={{display:'flex', flexDirection:'column', gap:8, fontSize:12, color:'#D4C9B2', lineHeight:1.55}}>
            <div style={{display:'flex', gap:10}}><span style={{color:'var(--orange-300)', fontWeight:800}}>1</span><span>역할 선택 + 본인 정보 입력</span></div>
            <div style={{display:'flex', gap:10}}><span style={{color:'var(--orange-300)', fontWeight:800}}>2</span><span>매니저·기관은 권역 관리자 승인 대기</span></div>
          </div>
        </div>
      </div>

      {/* Right — form */}
      {/* noValidate: 브라우저 기본 검증(required 풍선)을 끄고, 빈 항목 빨간 테두리 +
          첫 항목 포커스라는 커스텀 검증으로 일원화한다. */}
      <form ref={formRef} noValidate onSubmit={submit} className="signup-panel">
        <div style={{display:'flex', alignItems:'center', gap:10, fontSize:12, color:'var(--ink-3)'}}>
          {/* 브랜드 패널이 숨는 좁은 화면에서만 노출되는 로고(홈으로 나가는 경로) */}
          <a href="/" className="signup-mobile-logo" style={{alignItems:'center', gap:9, textDecoration:'none', color:'inherit'}}>
            <div className="brand-mark" style={{width:28, height:28}}>{brandParts(siteName()).mark}</div>
            <div style={{fontWeight:800, fontSize:14, color:'var(--ink-1)'}}>{brandParts(siteName()).name}</div>
          </a>
          <div style={{flex:1}}/>
          <span>이미 회원이신가요? <a href="/login" style={{color:'var(--orange-600)', fontWeight:700, marginLeft:6, textDecoration:'none'}}>로그인 →</a></span>
        </div>
        <div style={{margin:'32px 0 22px'}}>
          <h2 style={{fontSize:24, fontWeight:800, letterSpacing:'-0.02em', margin:0}}>회원가입</h2>
          <p style={{color:'var(--ink-3)', fontSize:13, marginTop:8}}>역할을 선택하고 본인 정보를 입력하세요.</p>
        </div>

        {err && <div style={{padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5, marginBottom:16, whiteSpace:'pre-line'}}>{err}</div>}

        {/* Role select */}
        <div className="label" style={{marginBottom:10}}>역할 선택</div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:10, marginBottom:22}}>
          {ROLES.map(r => {
            const active = role === r.v;
            const disabled = !r.canSelf;
            return (
              <div key={r.v}
                onClick={() => !disabled && setRole(r.v)}
                className="card"
                style={{
                  padding:14, borderRadius:12,
                  border: `1.5px solid ${active && !disabled ? `var(--${r.color}-500)` : 'var(--border)'}`,
                  background: active && !disabled ? `var(--${r.color}-50)` : 'var(--surface)',
                  opacity: disabled ? 0.55 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  display:'flex', gap:10, alignItems:'center', position:'relative',
                }}>
                <span style={{width:32, height:32, borderRadius:10,
                  background: active && !disabled ? `var(--${r.color}-500)` : 'var(--bg-sub)',
                  color: active && !disabled ? '#fff' : 'var(--ink-2)',
                  display:'grid', placeItems:'center'}}>
                  <span style={{width:18,height:18,display:'inline-block'}}>{Ic[r.icon]}</span>
                </span>
                <div style={{minWidth:0, flex:1}}>
                  <div style={{fontWeight:700, fontSize:13, display:'flex', alignItems:'center', gap:5}}>
                    {r.label}
                    {r.needsApproval && <span style={{padding:'1px 6px', fontSize:9, fontWeight:700, borderRadius:99, background:'var(--amber-50)', color:'#8a6326'}}>승인</span>}
                  </div>
                  <div style={{fontSize:10, color:'var(--ink-3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{r.sub}</div>
                </div>
                {active && !disabled && (
                  <span style={{position:'absolute', top:8, right:8, width:18, height:18, borderRadius:999, background:`var(--${r.color}-500)`, color:'#fff', display:'grid', placeItems:'center'}}>
                    <span style={{width:12, height:12, display:'inline-block'}}>{Ic.check}</span>
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {!roleMeta.canSelf && (
          <div style={{padding:'12px 14px', background:'var(--amber-50)', color:'#8a6326', borderRadius:10, fontSize:12, marginBottom:16}}>
            ⓘ 관리자 계정은 운영자 초대를 통해서만 가입할 수 있습니다.
          </div>
        )}

        {/* Common fields — 좁은 폭에서는 1열로 접힌다(2열 고정 시 입력칸이 짜부라짐) */}
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:14}}>
          <div>
            <div className="label">이름</div>
            <input className="input" name="name" data-field="name" style={eStyle('name')} value={form.name} onChange={e=>set('name')(e.target.value)} required/>
          </div>
          <div>
            <div className="label">연락처</div>
            <PhoneInput name="phone" data-field="phone" style={eStyle('phone')} value={form.phone} onChange={e=>set('phone')(e.target.value)}/>
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <div className="label">이메일 (로그인 ID)</div>
            <input className="input" name="email" data-field="email" style={eStyle('email')} type="email" value={form.email} onChange={e=>set('email')(e.target.value)} required/>
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <div className="label">비밀번호 (6자 이상, 영문+숫자 포함)</div>
            <PasswordInput name="password" data-field="password" error={!!errs.password} value={form.password} onChange={e=>set('password')(e.target.value)} required minLength={6}/>
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <div className="label">비밀번호 확인 (다시 한 번 입력)</div>
            <PasswordInput name="password_confirm" data-field="password_confirm" autoComplete="new-password"
              error={!!errs.password_confirm}
              value={passwordConfirm}
              onChange={e=>{ setPasswordConfirm(e.target.value); clearErr('password_confirm'); }} required minLength={6}/>
            {/* 둘 다 입력됐는데 다르면 즉시 안내(제출 전에도 보이도록). */}
            {passwordConfirm && form.password !== passwordConfirm && (
              <div style={{fontSize:11.5, color:'var(--rose-500)', marginTop:4}}>비밀번호가 일치하지 않습니다.</div>
            )}
          </div>

          {/* Citizen — 어르신 본인 (어르신 정보 + 보호자 정보 필수) */}
          {role === "citizen" && <>{elderlyFields(false)}{guardianFields}</>}

          {/* Guardian — 보호자 */}
          {role === "guardian" && <>
            <div style={{gridColumn:'1/-1'}}>
              <div className="label">어르신과의 관계</div>
              <select className="select" data-field="relation" style={eStyle('relation')} value={form.relation} onChange={e=>set('relation')(e.target.value)}>
                <option value="">— 선택 —</option>
                <option>자녀</option><option>배우자</option><option>형제·자매</option><option>친척</option><option>기타</option>
              </select>
            </div>
            <div style={{gridColumn:'1/-1'}}>
              <div className="label">어르신 연결 방식</div>
              <div style={{display:'flex', gap:8}}>
                {[["new","신규 등록"],["existing","연결코드로 연결"]].map(([v,l]) => (
                  <button type="button" key={v} onClick={()=>set('connect_mode')(v)}
                    style={{flex:1, padding:'8px 10px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
                      border:`1.5px solid ${form.connect_mode===v?'var(--rose-500)':'var(--border)'}`,
                      background: form.connect_mode===v?'var(--rose-50)':'var(--surface)',
                      color: form.connect_mode===v?'var(--rose-500)':'var(--ink-2)'}}>{l}</button>
                ))}
              </div>
            </div>
            {form.connect_mode === "existing" ? (
              <div style={{gridColumn:'1/-1'}}>
                <div className="label">연결코드</div>
                <input className="input" data-field="connect_code" style={eStyle('connect_code')} value={form.connect_code} onChange={e=>set('connect_code')(e.target.value)} placeholder="매니저·권역에서 발급받은 코드"/>
              </div>
            ) : elderlyFields(true)}
          </>}

          {/* Manager — 케어매니저 */}
          {role === "manager" && <>
            <div>
              <div className="label">자격증</div>
              <select className="select" data-field="qualification" style={eStyle('qualification')} value={form.qualification} onChange={e=>set('qualification')(e.target.value)}>
                <option value="">— 선택 —</option>
                <option>사회복지사 1급</option><option>사회복지사 2급</option>
                <option>요양보호사 1급</option><option>요양보호사 2급</option>
                <option>물리치료사</option><option>간호조무사</option>
              </select>
            </div>
            <div>
              <div className="label">나이</div>
              <input className="input" data-field="age" style={eStyle('age')} type="number" min={18} max={100}
                     value={form.age} onChange={e=>set('age')(e.target.value)} placeholder="세"/>
            </div>
            <div>
              <div className="label">자격증 사진 (자격증·발급번호가 보이게 1장)</div>
              <input className="input" type="file" accept="image/*,application/pdf"
                     data-field="license" style={eStyle('license')}
                     onChange={e=>{ setLicenseFile(e.target.files?.[0] || null); clearErr('license'); }}/>
              {licenseFile && <div className="muted" style={{fontSize:10, marginTop:4}}>첨부됨: {licenseFile.name}</div>}
            </div>
            <div>
              <div className="label">소속 기관 (필수)</div>
              {/* 케어매니저는 요양/재가(care) 기관에만 소속 가능. care 기관이 수천 건이라
                  native <select>(전체 옵션 렌더)는 느리다 → 검색형 OrgCombobox로 좁혀 고른다
                  (권역관리자 케어매니저 관리와 동일 컴포넌트). 기관을 고르면 권역은 그 기관의
                  권역으로 자동 설정된다(아래 '소속 권역'은 읽기 전용). */}
              <OrgCombobox
                orgs={orgs.filter(o => o.category === 'care')}
                value={form.organization_id ? Number(form.organization_id) : null}
                onChange={id => {
                  const org = id != null ? orgs.find(o => o.id === id) : null;
                  set('organization_id')(id != null ? String(id) : "");
                  set('region_id')(org && org.region_id != null ? String(org.region_id) : "");
                }}
                placeholder="기관 이름 검색 (필수)"
                width="100%"
                invalid={!!errs.organization_id}
              />
              {orgs.filter(o => o.category === 'care').length === 0 && (
                <div className="muted" style={{fontSize:10, marginTop:4, color:'var(--rose-500)'}}>
                  선택 가능한 요양/재가 기관이 없습니다. 운영자에게 기관 등록을 요청하세요.
                </div>
              )}
            </div>
            <div>
              <div className="label">소속 권역 (소속 기관에 따라 자동)</div>
              <input className="input" disabled
                     value={regions.find(r => String(r.id) === String(form.region_id))?.name || '(기관 선택 시 자동)'}
                     style={{background:'var(--bg-sub)', color:'var(--ink-2)', cursor:'not-allowed'}}/>
            </div>
          </>}

          {/* Agency — 유관기관 담당 */}
          {role === "agency" && <>
            <div>
              <div className="label">기관 내 직책</div>
              <input className="input" data-field="job_title" style={eStyle('job_title')} value={form.job_title} onChange={e=>set('job_title')(e.target.value)} placeholder="예: 사회복지팀장"/>
            </div>
            <div>
              <div className="label">나이</div>
              <input className="input" data-field="age" style={eStyle('age')} type="number" min={18} max={100}
                     value={form.age} onChange={e=>set('age')(e.target.value)} placeholder="세"/>
            </div>
            <div style={{gridColumn:'1/-1'}}>
              <div className="label">소속 기관</div>
              {/* 기존 기관을 고르면 권역은 그 기관의 권역으로 자동 설정(아래 읽기 전용).
                  신규 등록이면 권역을 직접 골라야 한다 — 가입 승인이 그 권역 관리자에게 간다.
                  유관기관은 병원·복지시설 등 전국 수만 건이라 native <select>는 더더욱 무겁다 →
                  검색형 OrgCombobox로 좁혀 고른다. allLabel(미선택=신규 등록)로 목록에 없을 때 처리. */}
              <OrgCombobox
                orgs={orgs}
                value={form.organization_id ? Number(form.organization_id) : null}
                onChange={id => {
                  const org = id != null ? orgs.find(o => o.id === id) : null;
                  set('organization_id')(id != null ? String(id) : "");
                  set('region_id')(org && org.region_id != null ? String(org.region_id) : "");
                }}
                allLabel="— 목록에 없음 (신규 등록) —"
                placeholder="기관 이름 검색"
                width="100%"
              />
            </div>
            {!form.organization_id ? <>
              <div>
                <div className="label">신규 기관명</div>
                <input className="input" data-field="organization_name" style={eStyle('organization_name')} value={form.organization_name} onChange={e=>set('organization_name')(e.target.value)} />
              </div>
              <div>
                <div className="label">기관 분류</div>
                <select className="select" data-field="organization_category" style={eStyle('organization_category')} value={form.organization_category} onChange={e=>set('organization_category')(e.target.value)}>
                  <option value="">— 선택 —</option>
                  <option value="hospital">병원</option>
                  <option value="health">보건소</option>
                  <option value="welfare">복지관</option>
                  <option value="care">요양시설</option>
                  <option value="fire">소방·119</option>
                </select>
              </div>
              <div style={{gridColumn:'1/-1'}}>
                <div className="label">소속 권역</div>
                <select className="select" data-field="region_id" style={eStyle('region_id')} value={form.region_id} onChange={e=>set('region_id')(e.target.value)}>
                  <option value="">— 선택 —</option>
                  {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            </> : (
              <div style={{gridColumn:'1/-1'}}>
                <div className="label">소속 권역 (소속 기관에 따라 자동)</div>
                <input className="input" disabled
                       value={regions.find(r => String(r.id) === String(form.region_id))?.name || '(권역 미지정 기관)'}
                       style={{background:'var(--bg-sub)', color:'var(--ink-2)', cursor:'not-allowed'}}/>
              </div>
            )}
          </>}
        </div>

        {/* Consents — 동의 3종 분리 (법적 요건) */}
        <div style={{marginTop:18, display:'flex', flexDirection:'column', gap:8}}>
          {[
            ["terms","이용약관에 동의합니다. (필수)"],
            ["privacy","개인정보 수집·이용에 동의합니다. (필수)"],
            ["sensitive","민감정보(건강·돌봄) 처리에 동의합니다. (필수)"],
          ].map(([k,l]) => (
            <label key={k} style={{display:'flex', gap:8, alignItems:'flex-start', fontSize:11.5, color:'var(--ink-3)', cursor:'pointer'}}>
              <input type="checkbox" checked={consent[k]} onChange={e=>setConsent(c=>({...c,[k]:e.target.checked}))} style={{marginTop:2}}/>
              <span>{l}</span>
            </label>
          ))}
          {roleMeta.needsApproval && (
            <div style={{fontSize:11, color:'#8a6326', marginTop:2}}>※ 매니저·유관기관 가입은 권역 관리자 승인 후 활성화됩니다.</div>
          )}
        </div>

        {!allAgreed && (
          <div style={{marginTop:10, fontSize:11.5, color:'var(--rose-500)'}}>필수 동의 항목에 모두 동의해야 가입할 수 있습니다.</div>
        )}

        <button type="submit" disabled={submitting || !roleMeta.canSelf} className="btn btn-primary btn-lg" style={{marginTop:20, width:'100%'}}>
          {submitting ? '처리 중…' : (roleMeta.needsApproval ? '가입 신청 → 권역 승인 대기' : '가입하고 시작하기 →')}
        </button>
      </form>
    </div>
    <AppFooter/>
    </div>
  );
}
export { Signup };
